// src/pages/ProfilePage.js
import React from "react";
import { useAuth } from "../contexts/AuthContext";
import TigerLogo from "../components/common/TigerLogo";
import { IconUser, IconShield, IconLayers } from "../components/common/Icons";

export default function ProfilePage() {
  const { user } = useAuth();
  if (!user) return null;

  const teamPerms = user.team_permissions || {};
  const hasTeams = !teamPerms._superuser && Object.keys(teamPerms).length > 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      {/* Header card */}
      <div className="rounded-2xl bg-onyx-900/60 border border-onyx-800/40 overflow-hidden">
        <div className="relative h-32 bg-gradient-to-br from-tiger-600/20 to-onyx-900 overflow-hidden">
          <div className="absolute inset-0 stripe-pattern opacity-40" />
          <div className="absolute bottom-0 right-6 opacity-10"><TigerLogo size={120} /></div>
        </div>
        <div className="relative px-6 pb-6">
          <div className="flex items-end gap-5 -mt-10">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-tiger-500 to-tiger-700 flex items-center justify-center border-4 border-onyx-900 shadow-lg">
              <span className="text-2xl font-display font-bold text-white">{user.username?.[0]?.toUpperCase()}</span>
            </div>
            <div className="pb-1">
              <h1 className="font-display text-xl font-bold text-onyx-100 tracking-wide">{user.username}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-tiger-500/15 text-tiger-400 border border-tiger-500/20">
                  {user.level_name}
                </span>
                <span className="text-xs font-mono text-onyx-500">Level {user.level}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Global Permissions */}
      <div className="mt-6 rounded-xl bg-onyx-900/40 border border-onyx-800/30 p-5">
        <div className="flex items-center gap-2 mb-4">
          <IconShield size={18} className="text-tiger-500" />
          <h2 className="font-display font-semibold text-onyx-200 tracking-wider text-sm">GLOBAL PERMISSIONS</h2>
          <span className="text-xs text-onyx-600">({user.global_permissions?.length || 0})</span>
        </div>
        {user.is_level_superuser ? (
          <p className="text-sm text-tiger-400/80 font-body">As a superuser, you have access to everything across all teams.</p>
        ) : user.global_permissions?.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {user.global_permissions.map((p) => (
              <span key={p} className="px-3 py-1 text-sm font-mono bg-onyx-800/50 text-onyx-300 rounded-lg border border-onyx-700/20">{p}</span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-onyx-600 font-body">No global permissions. Your access may be team-specific.</p>
        )}
      </div>

      {/* Team Permissions */}
      {hasTeams && (
        <div className="mt-4 rounded-xl bg-onyx-900/40 border border-onyx-800/30 p-5">
          <div className="flex items-center gap-2 mb-4">
            <IconUser size={18} className="text-blue-400" />
            <h2 className="font-display font-semibold text-onyx-200 tracking-wider text-sm">TEAM PERMISSIONS</h2>
          </div>
          <div className="space-y-3">
            {Object.values(teamPerms).map((tp) => (
              <div key={tp.team_id} className="p-3 rounded-lg bg-onyx-800/30 border border-onyx-700/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-display font-semibold text-blue-400">{tp.team_name}</span>
                  <span className="text-xs text-onyx-500">{tp.sport}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tp.permissions.map((p) => (
                    <span key={p} className="px-2 py-0.5 text-xs font-mono bg-blue-500/10 text-blue-300 rounded">{p}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Groups */}
      <div className="mt-4 rounded-xl bg-onyx-900/40 border border-onyx-800/30 p-5">
        <div className="flex items-center gap-2 mb-4">
          <IconLayers size={18} className="text-tiger-500" />
          <h2 className="font-display font-semibold text-onyx-200 tracking-wider text-sm">ROLE GROUPS</h2>
        </div>
        {user.groups?.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {user.groups.map((g) => (
              <span key={g} className="px-3 py-1 text-sm font-body bg-tiger-500/10 text-tiger-400 rounded-lg border border-tiger-500/15">{g}</span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-onyx-600 font-body">No group memberships.</p>
        )}
      </div>
    </div>
  );
}
